import React, { useEffect, useState } from 'react';
import { CircleX, PlusCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { uniq } from 'lodash';
import { Button, Label, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DropdownMenu, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuContent, Tooltip, TooltipProvider, TooltipTrigger, TooltipContent, } from '@cargomatic/ui';
import { showToast } from 'stores/ToastStore';
import { TERMINAL_HOLD_STATUS_VALUES } from 'constants/terminalHoldStatus';
import { getShipment } from 'utils/ShipmentWebAPIUtils';
import { sluggify } from 'utils/localization/utils';
import { updateTerminalHoldStatus } from 'utils/TerminalHoldStatusAPIUtils';
export function TerminalHoldStatusModal({ shipmentId = '', open = false, onHide, onSuccess, }) {
    const { t } = useTranslation();
    const [selectedTerminalHoldStatus, setSelectedTerminalHoldStatus] = useState([]);
    const [fetchFailed, setFetchFailed] = useState(false);
    /**
     * Get Existing Teminal Hold Status
     */
    const getShipmentDetails = async () => {
        try {
            const { entity: { item: shipment }, } = await getShipment(shipmentId);
            if (!shipment) {
                throw new Error(t('SHIPMENT_NOT_FOUND'));
            }
            setSelectedTerminalHoldStatus((shipment === null || shipment === void 0 ? void 0 : shipment.terminal_hold_status) || []);
        }
        catch (error) {
            showToast(t('SHIPMENT_NOT_FOUND'), null, 'error');
            setFetchFailed(true);
        }
    };
    useEffect(() => {
        if (open && shipmentId) {
            getShipmentDetails();
        }
    }, [shipmentId, open]);
    /**
     * Check if Terminal Hold Status is not present!
     */
    function validate() {
        const errors = {};
        if (!(selectedTerminalHoldStatus === null || selectedTerminalHoldStatus === void 0 ? void 0 : selectedTerminalHoldStatus.length)) {
            errors.terminalHoldStatus = `${t('TERMINAL_HOLD_STATUS')} ${t('REQUIRED')}`;
        }
        return errors;
    }
    /**
     * Handle removal of Terminal Hold Status existing list
     */
    function handleRemoveTerminalHoldStatus(status) {
        return () => {
            setSelectedTerminalHoldStatus((items) => items.filter((item) => item !== status));
        };
    }
    /**
     * Handle toggle of Terminal Hold Status in dropdown
     */
    function handleToggleTerminalHoldStatus(status) {
        return (checked) => {
            if (checked) {
                setSelectedTerminalHoldStatus((prev) => uniq(prev.concat(status)));
            }
            else {
                setSelectedTerminalHoldStatus((prev) => prev.filter((current) => current !== status));
            }
        };
    }
    /**
     * Apply validations and make an update request
     */
    async function handleSave() {
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            return;
        }
        try {
            const payload = [
                {
                    job_id: shipmentId,
                    updates: {
                        terminal_hold_status: selectedTerminalHoldStatus,
                    },
                },
            ];
            await updateTerminalHoldStatus(payload);
            if (onSuccess) {
                onSuccess({ terminal_hold_status: selectedTerminalHoldStatus });
            }
            showToast(t('TERMINAL_HOLD_STATUS_SUCCESS_UPDATE'));
            setSelectedTerminalHoldStatus([]);
            onHide();
        }
        catch (e) {
            showToast(t('TERMINAL_HOLD_STATUS_UPDATE_ERROR'), null, 'error');
        }
    }
    return (React.createElement(Dialog, { open: open, onOpenChange: onHide, className: "z-50" },
        React.createElement(DialogContent, { className: "sm:max-w-[600px]" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, t('UPDATE_TERMINAL_HOLD_STATUS'))),
            React.createElement("div", { className: "space-y-4 py-4" },
                React.createElement("div", { className: "" },
                    React.createElement(Label, { htmlFor: "reason-code", className: "block mb-1" },
                        t('TERMINAL_HOLD_STATUS'),
                        " *"),
                    selectedTerminalHoldStatus.length > 0 && (React.createElement("ul", { className: "mb-4 space-y-1 max-h-72 overflow-y-auto" }, selectedTerminalHoldStatus.map((item, index) => (React.createElement("li", { key: item, className: "flex justify-between items-center" },
                        React.createElement("span", null, t(sluggify(item))),
                        React.createElement(TooltipProvider, null,
                            React.createElement(Tooltip, null,
                                React.createElement(TooltipTrigger, { asChild: true },
                                    React.createElement(Button, { "data-testid": `ths-item-btn-${index}`, variant: "icon", size: "icon", className: "hover:text-red-500", onClick: handleRemoveTerminalHoldStatus(item) },
                                        React.createElement(CircleX, { className: "w-4 h-4" }))),
                                React.createElement(TooltipContent, null, t('CLICK_TO_REMOVE_TERMINAL_HOLD_STATUS'))))))))),
                    React.createElement(DropdownMenu, { className: "mb-4" },
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { "data-testid": "ths-dropdown-btn", variant: "outline", size: "sm", className: "h-10 gap-1" },
                                React.createElement("span", { className: "sr-only sm:not-sr-only sm:whitespace-nowrap" }, t('SELECT_TERMINAL_HOLD_STATUS')),
                                React.createElement(PlusCircleIcon, { className: "h-3.5 w-3.5" }))),
                        React.createElement(DropdownMenuContent, { align: "end", className: "max-h-72 overflow-y-auto" },
                            React.createElement(DropdownMenuLabel, null, t('SELECT_TERMINAL_HOLD_STATUS')),
                            React.createElement(DropdownMenuSeparator, null), TERMINAL_HOLD_STATUS_VALUES === null || TERMINAL_HOLD_STATUS_VALUES === void 0 ? void 0 :
                            TERMINAL_HOLD_STATUS_VALUES.map((terminalHoldStatus, index) => (React.createElement(DropdownMenuCheckboxItem, { "data-testid": `ths-dropdown-option-${index}`, key: terminalHoldStatus, onCheckedChange: handleToggleTerminalHoldStatus(terminalHoldStatus), checked: selectedTerminalHoldStatus.includes(terminalHoldStatus) }, t(sluggify(terminalHoldStatus))))))))),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { "data-testid": "ths-submit-btn", disabled: !(selectedTerminalHoldStatus === null || selectedTerminalHoldStatus === void 0 ? void 0 : selectedTerminalHoldStatus.length) || fetchFailed, className: "disabled:opacity-50 disabled:cursor-not-allowed", type: "submit", onClick: handleSave }, t('SAVE'))))));
}
